<template>
  <div v-if="mounted" class="admin-page">
    <h1>Create New Dataset</h1>

    <form @submit.prevent="handleSubmit">
      <div v-if="errors.general" class="form-error">{{ errors.general }}</div>

      <div class="form-field">
        <label for="id">Dataset ID</label>
        <input
          id="id"
          type="text"
          v-model="form.id"
          placeholder="e.g., dataset-2024-12. Leave blank to auto-generate"
        />
        <div v-if="errors.id" class="form-error">{{ errors.id }}</div>
      </div>

      <div class="form-field">
        <label for="description">Description *</label>
        <textarea
          id="description"
          v-model="form.description"
          rows="3"
          required
          placeholder="Description of this dataset"
        ></textarea>
        <div v-if="errors.description" class="form-error">{{ errors.description }}</div>
      </div>

      <div class="form-field">
        <label for="email">Email *</label>
        <input
          id="email"
          type="email"
          v-model="form.email"
          required
          placeholder="user@example.com"
        />
        <div v-if="errors.email" class="form-error">{{ errors.email }}</div>
      </div>

      <div class="form-field">
        <label for="groupSet">Group Set</label>
        <input
          id="groupSet"
          type="text"
          v-model="form.groupSet"
          placeholder="Comma-separated group IDs (e.g., group1, group2)"
        />
        <small class="field-hint">Leave empty to process all groups.</small>
      </div>

      <div class="form-field">
        <label for="storeSet">Store Set</label>
        <input
          id="storeSet"
          type="text"
          v-model="form.storeSet"
          placeholder="Comma-separated store IDs (e.g., store1, store2)"
        />
        <small class="field-hint">Leave empty to process all stores.</small>
      </div>

      <div class="form-field">
        <label class="checkbox-label">
          <input type="checkbox" v-model="form.devMode" />
          Dev Mode
        </label>
        <small class="field-hint">When enabled, all customer names will be randomised, but, customer and products codes and their related values will remain intact.</small>
      </div>

      <div class="form-field">
        <label for="devModeSeed">Dev Mode Seed</label>
        <input
          id="devModeSeed"
          type="number"
          v-model.number="form.devModeSeed"
          :disabled="!form.devMode"
        />
        <small class="field-hint">For dev mode, the same integer seed on the same data will produce the same 'randomised' customer names.</small>
      </div>

      <div class="action-row">
        <button type="submit" :disabled="submitting">
          {{ submitting ? "Creating..." : "Create and process dataset" }}
        </button>
        <button type="button" @click="handleCancel" :disabled="submitting">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { ValueService } from "@/services";
import type { Dataset } from "@/data";

const router = useRouter();
const service = new ValueService();

const mounted = ref(false);
const submitting = ref(false);
const errors = ref<Record<string, string>>({});

const form = ref({
  id: "",
  description: "",
  email: "",
  groupSet: "",
  storeSet: "",
  devMode: false,
  devModeSeed: 0
});

function validateForm(): boolean {
  errors.value = {};

  if (!form.value.email.trim()) {
    errors.value.email = "Email is required";
    return false;
  }

  if (!form.value.description.trim()) {
    errors.value.email = "Description is required";
    return false;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(form.value.email)) {
    errors.value.email = "Please enter a valid email address";
    return false;
  }

  return true;
}

async function handleSubmit() {
  if (!validateForm()) {
    return;
  }

  errors.value.general = "";
  submitting.value = true;

  try {
    const dataset: Partial<Dataset> = {
      id: form.value.id.trim(),
      email: form.value.email.trim()
    };

    if (form.value.description.trim()) {
      dataset.description = form.value.description.trim();
    }

    if (form.value.groupSet.trim()) {
      dataset.groupSet = form.value.groupSet.trim();
    }

    if (form.value.storeSet.trim()) {
      dataset.storeSet = form.value.storeSet.trim();
    }

    if (form.value.devMode) {
      dataset.devMode = true;
      dataset.devModeSeed = form.value.devModeSeed;
    }

    await service.createDataset(dataset);
    router.push({ name: "datasets-list" });
  } catch (error: any) {
    errors.value.general = error.message || "Failed to create dataset";
  } finally {
    submitting.value = false;
  }
}

function handleCancel() {
  router.push({ name: "datasets-list" });
}

async function loadUserEmail() {
  try {
    const user = await service.fetchCurrentUser();
    if (user?.email) {
      form.value.email = user.email;
    }
  } catch (error: any) {
    console.error("Failed to fetch user email:", error);
  }
}

onMounted(async () => {
  await loadUserEmail();
  mounted.value = true;
});
</script>

<style scoped src="../admin.css"></style>

<style scoped>
textarea {
  resize: vertical;
  min-height: 4rem;
}

.field-hint {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #6b7280;
  font-style: italic;
}

input:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.checkbox-label {
  display: inline-flex !important;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  cursor: pointer;
}

button[type="button"] {
  background-color: #6b7280;
}

button[type="button"]:hover {
  background-color: #4b5563;
}
</style>
